/**
 * WU-07: capability → 表示可能 ScreenId の変換（純関数）と、
 * WU-04 の enabledScreens（deploy 単位の画面セット）との合成。
 *
 * サーバー契約: GET /api/admin/me/bootstrap（packages/core/src/api/web-ui/admin.ts）
 * capability の正本: packages/core/src/api/web-ui/capabilities.ts（同期必須）
 *
 * nav filter / route guard は fail-closed:
 * - bootstrap 取得失敗（null）→「見る」系のみ（全画面露出にしない）
 * - legacy API_TOKEN / preview auth 判定時も会議画面だけは除外する
 * 権限境界そのものはサーバー側 403（WU-02）が担う。
 */
import { ALL_SCREEN_IDS, type EnabledScreens, type ScreenId } from './nav-config'

/** GET /admin/me/bootstrap のレスポンス契約（core admin.ts と同期） */
export interface MeBootstrapResponse {
  userId: string
  primaryWorkspaceId: string | null
  role: string | null
  capabilities: string[]
  authSource?: 'clerk' | 'legacy'
}

/**
 * capability → 表示可能 ScreenId の対応表。
 * 全 ScreenId をいずれかの capability がカバーする（contract test で担保）。
 * 未知の capability は無視する（前方互換: サーバーが新 capability を返しても壊れない）。
 */
export const CAPABILITY_SCREENS: Readonly<Record<string, readonly ScreenId[]>> = {
  'screens.view': ['dashboard', 'kpi', 'mastery', 'proposals'],
  'meetings.view': ['weekly-status', 'meeting-center', 'evidence-history'],
  'operations.manage': ['gift-operations'],
  'admin.manage': [
    'adapters',
    'acl',
    'onboarding',
    'monitoring',
    'box',
    'memory-explore',
    'admin',
    'stats',
  ],
  'members.manage': ['workspaces'],
  'keys.manage': ['access-management'],
  'curation.view': ['benchmark-curation', 'j-locomo-curation'],
}

/** bootstrap 取得失敗時の fail-closed fallback（「見る」系のみ） */
export const FALLBACK_CAPABILITIES: readonly string[] = ['screens.view']

/** capability リスト → 表示可能 ScreenId セット（未知 capability は無視） */
export function screensForCapabilities(
  capabilities: readonly string[],
): ReadonlySet<ScreenId> {
  const screens = new Set<ScreenId>()
  for (const capability of capabilities) {
    for (const screen of CAPABILITY_SCREENS[capability] ?? []) {
      screens.add(screen)
    }
  }
  return screens
}

/**
 * bootstrap 結果 → capability 由来の画面セット（EnabledScreens 互換の表現）。
 *
 * - legacyOrPreviewAuth: true → 従来画面のみ（会議画面は除外。preview auth / legacy
 *   API_TOKEN 経路から根拠付き会議データを露出しない）
 * - bootstrap null（取得失敗 / 未取得）→ FALLBACK_CAPABILITIES の画面のみ（fail-closed）
 * - それ以外 → capabilities から変換
 */
export function capabilityScreens(
  bootstrap: MeBootstrapResponse | null,
  options?: { legacyOrPreviewAuth?: boolean },
): EnabledScreens {
  if (options?.legacyOrPreviewAuth || bootstrap?.authSource === 'legacy') {
    const protectedScreens = new Set<ScreenId>([
      'weekly-status',
      'meeting-center',
      'evidence-history',
      'gift-operations',
    ])
    return new Set(ALL_SCREEN_IDS.filter((screen) => !protectedScreens.has(screen)))
  }
  if (bootstrap === null) {
    return screensForCapabilities(FALLBACK_CAPABILITIES)
  }
  return screensForCapabilities(bootstrap.capabilities)
}

/**
 * capability 由来セットと WU-04 enabledScreens の積集合。
 * null は「制約なし」。両方 null なら null（全画面 = 既存挙動）。
 * 積が空になった場合も空セットのまま返す（fail-closed。null に昇格させない）。
 */
export function combineWithEnabledScreens(
  capability: EnabledScreens,
  enabled: EnabledScreens,
): EnabledScreens {
  if (capability === null) return enabled
  if (enabled === null) return capability
  const intersection = new Set<string>()
  for (const screen of capability) {
    if (enabled.has(screen)) {
      intersection.add(screen)
    }
  }
  return intersection
}

/** contract test 用: 全 ScreenId がいずれかの capability でカバーされているか */
export function uncoveredScreens(): readonly ScreenId[] {
  const covered = screensForCapabilities(Object.keys(CAPABILITY_SCREENS))
  return ALL_SCREEN_IDS.filter((screen) => !covered.has(screen))
}
